/**
 * TripsPage - Main page for viewing and managing trips
 * Redesigned to match TripListDashboard.html
 */

import { useState, useRef } from 'react';
import { useClickOutside } from '../hooks/useClickOutside';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTrips } from '../contexts/TripContext';
import { useToast } from '../contexts/ToastContext';
import type { Trip } from '../types/trip';
import TripList from '../components/trips/TripList';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingScreen from '../components/common/LoadingScreen';

type TabFilter = 'upcoming' | 'past' | 'all';

export default function TripsPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { trips, isLoading, error, deleteTrip } = useTrips();
  const { showToast } = useToast();
  const [deletingTrip, setDeletingTrip] = useState<Trip | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<TabFilter>('upcoming');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileUserMenuRef = useRef<HTMLDivElement>(null);

  const handleCreateTrip = () => {
    navigate('/trips/new');
  };

  const handleEditTrip = (trip: Trip) => {
    navigate(`/trips/${trip.id}/edit`);
  };

  const handleDeleteTrip = (trip: Trip) => {
    setDeletingTrip(trip);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTrip) return;
    try {
      await deleteTrip(deletingTrip.id);
      showToast('success', '已刪除旅程');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : '刪除旅程失敗');
    } finally {
      setDeletingTrip(undefined);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  useClickOutside(userMenuRef, () => setShowUserMenu(false), showUserMenu);

  // Filter trips by search query and tab
  const filteredTrips = trips.filter(trip => {
    const matchesSearch = trip.name.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (activeTab === 'all') return true;
    const now = new Date();
    const endDate = new Date(trip.endDate);
    const isUpcoming = endDate >= now;
    return activeTab === 'upcoming' ? isUpcoming : !isUpcoming;
  });

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center">
        <div className="text-red-600">錯誤: {error.message}</div>
      </div>
    );
  }

  const tabs: { key: TabFilter; label: string }[] = [
    { key: 'upcoming', label: '即將到來' },
    { key: 'past', label: '已完成' },
    { key: 'all', label: '全部' },
  ];

  return (
    <div className="relative w-full bg-background-light md:flex md:h-screen md:overflow-hidden">

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-60 md:shrink-0 md:h-screen md:sticky md:top-0 md:bg-white md:border-r md:border-gray-100">
        {/* Logo */}
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#0dccf2] rounded-lg flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M2.5 19h19v2h-19v-2zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06L14.92 10l-6.9-6.43-1.93.51 4.14 7.17-4.97 1.33-1.97-1.54-1.45.39 1.82 3.16.77 1.33 1.6-.43 5.31-1.42 4.35-1.16L21 11.49c.81-.23 1.28-1.05 1.07-1.85z"/>
              </svg>
            </div>
            <span className="font-bold text-lg text-[#111718]">Tripway</span>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex flex-col gap-1 p-3 flex-1">
          <a href="/trips" className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#0dccf2]/10 text-[#0dccf2] font-semibold text-sm">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
            </svg>
            首頁
          </a>
          <a href="#" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            行事曆
          </a>
          <a href="#" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            個人資料
          </a>
        </nav>

        {/* User info at bottom */}
        {user && (
          <div className="p-3 border-t border-gray-100 relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 w-full hover:bg-gray-50 rounded-lg p-2 transition-colors"
            >
              {user.avatar ? (
                <div
                  className="w-9 h-9 rounded-full bg-cover bg-center border-2 border-primary shrink-0"
                  style={{ backgroundImage: `url("${user.avatar}")` }}
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gray-100 border-2 border-primary flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-semibold text-[#111718] truncate">{user.nickname}</p>
              </div>
            </button>
            {showUserMenu && (
              <div className="absolute bottom-16 left-3 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  登出
                </button>
              </div>
            )}
          </div>
        )}
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-h-screen md:min-h-0 md:overflow-y-auto pb-24 md:pb-0">

        {/* Mobile Header */}
        <header className="md:hidden sticky top-0 z-20 flex items-center justify-between bg-white/90 backdrop-blur-md p-4 pb-2 border-b border-gray-100">
          <div className="flex size-12 shrink-0 items-center relative" ref={mobileUserMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="focus:outline-none hover:opacity-80 transition-opacity"
            >
              {user?.avatar ? (
                <div
                  className="bg-center bg-no-repeat aspect-square bg-cover rounded-full size-10 border-2 border-primary"
                  style={{ backgroundImage: `url("${user.avatar}")` }}
                />
              ) : (
                <div className="flex items-center justify-center rounded-full size-10 border-2 border-primary bg-gray-100">
                  <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
            </button>

            {showUserMenu && (
              <div className="absolute top-12 left-0 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                {user && (
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900 truncate">{user.nickname}</p>
                  </div>
                )}
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  登出
                </button>
              </div>
            )}
          </div>
          <h2 className="text-[#111718] text-xl font-bold leading-tight tracking-[-0.015em] flex-1 text-center">
            我的旅程
          </h2>
          <div className="flex w-12 items-center justify-end">
            <button className="flex items-center justify-center rounded-full size-10 text-[#111718] hover:bg-gray-100 transition-colors">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
        </header>

        {/* Desktop Header */}
        <header className="hidden md:flex items-center justify-between px-8 py-6 border-b border-gray-100 bg-background-light">
          <div>
            <h1 className="text-2xl font-bold text-[#111718]">我的旅程</h1>
            <p className="text-gray-500 text-sm mt-0.5">規劃您即將到來的冒險旅程</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                className="block rounded-lg border-0 bg-white py-2 pl-9 pr-3 text-[#111718] shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary text-sm w-56"
                placeholder="搜尋旅程名稱"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button
              onClick={handleCreateTrip}
              className="flex items-center gap-1.5 bg-[#0dccf2] text-white px-4 py-2 rounded-full font-semibold text-sm hover:bg-[#0dccf2]/90 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              新增旅程
            </button>
          </div>
        </header>

        {/* Mobile Search Bar */}
        <div className="md:hidden px-4 pt-4 bg-background-light">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              className="block w-full rounded-xl border-0 bg-white py-3 pl-10 pr-3 text-[#111718] shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
              placeholder="搜尋目的地或旅程名稱"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Mobile Tab Switcher */}
        <div className="md:hidden sticky top-[68px] z-10 bg-background-light px-4 py-3 pt-4">
          <div className="flex h-12 w-full items-center justify-center rounded-xl bg-gray-200 p-1 shadow-inner">
            <label className={`flex cursor-pointer h-full grow items-center justify-center overflow-hidden rounded-lg px-2 text-sm font-bold leading-normal transition-all duration-200 ${
              activeTab === 'upcoming'
                ? 'bg-white shadow-sm text-[#111718]'
                : 'text-gray-500'
            }`}>
              <span className="truncate">即將到來</span>
              <input
                checked={activeTab === 'upcoming'}
                className="invisible w-0 h-0 absolute"
                name="trip-view"
                type="radio"
                value="upcoming"
                onChange={() => setActiveTab('upcoming')}
              />
            </label>
            <label className={`flex cursor-pointer h-full grow items-center justify-center overflow-hidden rounded-lg px-2 text-sm font-bold leading-normal transition-all duration-200 ${
              activeTab === 'past'
                ? 'bg-white shadow-sm text-[#111718]'
                : 'text-gray-500'
            }`}>
              <span className="truncate">已完成</span>
              <input
                checked={activeTab === 'past'}
                className="invisible w-0 h-0 absolute"
                name="trip-view"
                type="radio"
                value="past"
                onChange={() => setActiveTab('past')}
              />
            </label>
          </div>
        </div>

        {/* Desktop Tab Bar */}
        <div className="hidden md:flex items-center gap-2 px-8 py-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-colors ${
                activeTab === tab.key
                  ? 'bg-[#0dccf2]/10 text-[#0dccf2]'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <main className="flex flex-col gap-4 p-4 md:px-8 md:py-6">
          <TripList trips={filteredTrips} onEditTrip={handleEditTrip} onDeleteTrip={handleDeleteTrip} />
        </main>

        {/* Floating Action Button - mobile only */}
        <button
          onClick={handleCreateTrip}
          aria-label="創建新旅程"
          className="md:hidden fixed bottom-24 right-4 z-30 flex items-center justify-center size-14 rounded-full bg-[#0dccf2] text-white shadow-lg shadow-[#0dccf2]/30 hover:bg-[#0dccf2]/90 hover:scale-105 active:scale-95 transition-all duration-200"
        >
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </button>

        {/* Bottom Navigation - mobile only */}
        <nav className="md:hidden fixed bottom-0 z-20 w-full bg-white/90 backdrop-blur-md border-t border-gray-100 pb-safe">
          <div className="flex justify-around items-center h-[70px] pb-2">
            <a className="flex flex-col items-center justify-center gap-1 p-2 min-w-[64px] text-[#0dccf2]" href="/trips">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
              </svg>
              <span className="text-[10px] font-bold">旅程</span>
            </a>
            <a className="flex flex-col items-center justify-center gap-1 p-2 min-w-[64px] text-gray-400 hover:text-gray-600 transition-colors" href="#">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
              <span className="text-[10px] font-medium">探索</span>
            </a>
            <a className="flex flex-col items-center justify-center gap-1 p-2 min-w-[64px] text-gray-400 hover:text-gray-600 transition-colors" href="#">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-[10px] font-medium">個人</span>
            </a>
          </div>
        </nav>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deletingTrip}
        onClose={() => setDeletingTrip(undefined)}
        onConfirm={handleConfirmDelete}
        title="刪除旅程"
        message={`確定要刪除「${deletingTrip?.name}」嗎？此操作將永久刪除該旅程及其所有活動，且無法復原。`}
        confirmText="刪除"
        cancelText="取消"
      />
    </div>
  );
}
